import {
  RECEIVE_PAYMENT_INFO,
  RECEIVE_PAYMENT_INFO_BY_ID,
  RECEIVE_UPDATE_PAYMENT_INFO_ERROR,
  REQUEST_PAYMENT_INFO,
  RESET_PAYMENT_FORM,
  RESET_SAVED_PAYMENT_INFO,
  UPDATE_CVV,
  UPDATE_IS_PAYMENT_PRIMARY,
  UPDATE_PAYMENT_BILLING_ADDRESS,
  UPDATE_PAYMENT_CARD_NUMBER,
  UPDATE_PAYMENT_EXPIRATION_MONTH,
  UPDATE_PAYMENT_EXPIRATION_YEAR,
  UPDATE_PAYMENT_FULL_NAME
} from 'constants/reduxActions';
import { prependAppRoot } from 'history/AppRootUtils';
import { authenticationErrorCatchHandlerFactory, err, setError } from 'actions/errors';
import { deletePaymentInstrument, paymentTypes, primaryPayment, savePayment } from 'apis/mafia';
import {
  fetchApiNotAuthenticatedMiddleware,
  fetchErrorMiddleware,
  fetchErrorMiddlewareMaybeJson,
  fetchMafiaValidationErrorMiddleware
} from 'middleware/fetchErrorMiddleware';
import { setSessionCookies } from 'actions/session';
import { redirectTo } from 'actions/redirect';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import { saveToLocalStorage } from 'helpers/localStorageUtilities';
import { selectMafiaConfig } from 'selectors/environment';

export function updateFullName(fullName) {
  return {
    type: UPDATE_PAYMENT_FULL_NAME,
    fullName
  };
}

export function updateCardNumber(addCreditCardNumber) {
  return {
    type: UPDATE_PAYMENT_CARD_NUMBER,
    addCreditCardNumber
  };
}
export function updateExpirationMonth(expirationMonth) {
  return {
    type: UPDATE_PAYMENT_EXPIRATION_MONTH,
    expirationMonth
  };
}

export function updateExpirationYear(expirationYear) {
  return {
    type: UPDATE_PAYMENT_EXPIRATION_YEAR,
    expirationYear
  };
}

export function updateCVV(cvv) {
  return {
    type: UPDATE_CVV,
    cvv
  };
}

export function updateBillingAddress(addressId) {
  return {
    type: UPDATE_PAYMENT_BILLING_ADDRESS,
    addressId
  };
}

export function updateIsPrimaryPayment(isPrimaryPayment) {
  return {
    type: UPDATE_IS_PAYMENT_PRIMARY,
    isPrimaryPayment
  };
}

export function requestPaymentInfo() {
  return {
    type: REQUEST_PAYMENT_INFO
  };
}

export function receivePaymentInfo(paymentInstruments) {
  return {
    type: RECEIVE_PAYMENT_INFO,
    paymentInstruments
  };
}

export function receivePaymentInfoById(paymentInstrument) {
  return {
    type: RECEIVE_PAYMENT_INFO_BY_ID,
    paymentInstrument
  };
}

export function resetSavedPaymentInfo() {
  return {
    type: RESET_SAVED_PAYMENT_INFO
  };
}

export function receiveUpdatePaymentInfoError(error) {
  return {
    type: RECEIVE_UPDATE_PAYMENT_INFO_ERROR,
    error
  };
}

export function fetchPaymentInfo(paymentId, returnTo) {
  return (dispatch, getState) => {
    dispatch(requestPaymentInfo());

    const state = getState();
    const {
      cookies,
      router: { location }
    } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return paymentTypes(mafiaConfig, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        const { paymentInstruments } = resp;
        if (paymentId) {
          const paymentInstrument = paymentInstruments.find(({ paymentInstrumentId }) => paymentInstrumentId === paymentId);
          if (paymentInstrument) {
            dispatch(receivePaymentInfoById(paymentInstrument));
          } else {
            dispatch(setError(err.GENERIC, new Error('Could not find specified paymentInstrumentId.')));
          }
        } else {
          dispatch(receivePaymentInfo(paymentInstruments));
        }
      })
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot(returnTo || '/payments', location)));
  };
}

export function updatePaymentInfo(card, addressId, returnTo, payment) {
  return function (dispatch, getState) {
    dispatch(requestPaymentInfo());

    const state = getState();
    const {
      cookies,
      router: { location }
    } = state;
    const mafiaConfig = selectMafiaConfig(state);

    // use v2 paymentInstruments endpoint only if a CVV is passed in (AddPayment vs EditPayment flows)
    const useV2PaymentInstruments = !!card.cvv;
    const params = {
      instrument: card,
      addressId,
      useV2PaymentInstruments
    };
    return savePayment(mafiaConfig, params, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchMafiaValidationErrorMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        dispatch(resetSavedPaymentInfo());
        dispatch(redirectTo(prependAppRoot(returnTo || '/payments', location)));
        dispatch(fetchPaymentInfo());
        return resp;
      })
      .catch(e => {
        dispatch(receiveUpdatePaymentInfoError(e));

        if (payment) {
          const { pathname } = location;
          const { fullName, expirationMonth, expirationYear, cvv } = payment;
          saveToLocalStorage('unsavedCardChanges', {
            fullName,
            expirationMonth,
            expirationYear,
            cvv
          });
          authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot(pathname || '/payments', location))(e);
        }
      });
  };
}

export function makePrimary(fullName, paymentId) {
  return function (dispatch, getState) {
    dispatch(requestPaymentInfo());

    const state = getState();
    const {
      cookies,
      router: { location }
    } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return primaryPayment(mafiaConfig, fullName, paymentId, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(() => dispatch(fetchPaymentInfo()))
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot('/payments', location)));
  };
}

export function deletePaymentInfo(paymentId) {
  return function (dispatch, getState) {
    dispatch(requestPaymentInfo());

    const state = getState();
    const {
      cookies,
      router: { location }
    } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return deletePaymentInstrument(mafiaConfig, paymentId, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddlewareMaybeJson)
      .then(() => dispatch(fetchPaymentInfo()))
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot('/payments', location)));
  };
}

export function resetPaymentForm() {
  return {
    type: RESET_PAYMENT_FORM
  };
}
